import type { Campaign } from '../types/campaign.js';
import type { FranchiseProfile } from '../types/franchiseProfile.js';

// Resolve the effective Meta naming template for a Campaign (Pre-P2b-3 §1.2).
// Pure: takes only the two source documents and returns the template string
// or null. Substitution is the templating helper's job; this function picks
// which template to feed it.
//
// Precedence:
//   1. campaign.metaCampaignNameTemplate (per-Campaign override)
//   2. brandProfile.defaultMetaCampaignNameTemplate (brand default)
//   3. null  →  caller renders "—" with tooltip "No naming template set"
//
// Both inputs accept null/undefined for legacy / missing data. Empty-string
// templates are treated as "set" (returning empty string) so the caller can
// distinguish "explicitly cleared" from "never authored". In practice the
// authoring forms write null for an empty input, so this distinction is
// theoretical for now — but locking the rule keeps the helper predictable.

export function effectiveMetaCampaignNameTemplate(
  campaign:
    | Pick<Campaign, 'metaCampaignNameTemplate'>
    | null
    | undefined,
  brandProfile:
    | Pick<FranchiseProfile, 'defaultMetaCampaignNameTemplate'>
    | null
    | undefined,
): string | null {
  const campaignOverride = campaign?.metaCampaignNameTemplate;
  if (campaignOverride !== null && campaignOverride !== undefined) {
    return campaignOverride;
  }
  const brandDefault = brandProfile?.defaultMetaCampaignNameTemplate;
  if (brandDefault !== null && brandDefault !== undefined) {
    return brandDefault;
  }
  return null;
}

// Pre-P2b-3 polish round 5 §2.2: Google ad-label template resolver.
// Parallel to effectiveMetaCampaignNameTemplate above — same precedence
// rule, same empty-string-is-set semantics. Surfaced by the row
// editor's Google Content section preview, the future P2b-3 Google
// grid column, and the Campaign editor's new "Google Naming" section.
export function effectiveGoogleAdLabelTemplate(
  campaign:
    | Pick<Campaign, 'googleAdLabelTemplate'>
    | null
    | undefined,
  brandProfile:
    | Pick<FranchiseProfile, 'defaultGoogleAdLabelTemplate'>
    | null
    | undefined,
): string | null {
  const campaignOverride = campaign?.googleAdLabelTemplate;
  if (campaignOverride !== null && campaignOverride !== undefined) {
    return campaignOverride;
  }
  const brandDefault = brandProfile?.defaultGoogleAdLabelTemplate;
  if (brandDefault !== null && brandDefault !== undefined) {
    return brandDefault;
  }
  return null;
}
